<template>
  <!------------------------------------------
    Map selecting a congressional district on the data snapshots landing page.
  -------------------------------------------->
  <div id="profiles-landing-page-map">
    <div>
      <MapTemplate :showLayerPanel="false" :mapContainerId="mapContainerId" :loading="loading" mapHeight="28rem" />
      <div id="map-tooltip">
        <div id="map-tooltip-title"></div>
        <div id="map-tooltip-subtitle"></div>
      </div>
    </div>
  </div>
</template>

<script>
import MapTemplate from '@/components/map/MapTemplate'
import bboxes from '@/js/bboxes.js'
import mapboxgl from "mapbox-gl"
import states from '@/js/stFips.js'

let map

export default {
  name: 'ProfilesLandingPageMap',

  components: { MapTemplate },

  props: {
    selectedState: { type: String, required: false },
    selectedDistrict: { type: String, required: false },
  },

  data () {
    return {
      loaded: false,
      newBBox: null, // When changing the bounding boxes, set the new bounding box to be newBBox to automatically re-fit the map
      padding: 25,
      accessToken: `${process.env.VUE_APP_MAPBOX_TOKEN}`,
      map: null,
      defaultBBox: [-124.736342, 24.521208, -66.945392, 49.382808], // contiguous US (used if no bbox provided)
      allowScrollZoom: false,
      loading: false,
      mapContainerId: 'map-container',
      layers: [
        { 
          tilesetId: 'lsc-odga.119th_congress',
          loadOnStart: true,
          filterOn: 'state_fips',
          layerDefs: {
            'id': '119th_congress-fill',
            'type': 'fill',
            'source': `119th_congress-fill-src`,
            'source-layer': '119th_congress',
            'paint': {
              'fill-opacity': 0.7,
              'fill-color': '#165991',
              'fill-outline-color': 'white'
            }
          }
        }
      ]
    }
  },

  computed: {
    bbox () { return this.newBBox === null || this.newBBox === undefined ? this.defaultBBox : this.newBBox },
    filterStateField () { 
      return this.selectedState !== null ? ["==", "state_fips", this.selectedState] : ['in', 'state_fips'].concat(this.allowedStateFips)
    },
    colorExp () { return ['match', ['get', 'geoid'], this.selectedDistrict, '#a9c9e6', '#165991'] },
    allowedStateFips () {
      return states.filter(x => ['PR', 'VI', 'AS', 'MP', 'GU'].indexOf(x.st) === -1).map(x => x.fips)
    }
  },

  async mounted () {
    // Initialize the map
    mapboxgl.accessToken = this.accessToken
    this.$nextTick(() => {
      this.mapSetup()
    })
    if (this.selectedState) {
      this.newBBox = bboxes.states[this.selectedState]
    }
  },

  methods: {
    mapSetup: function() { 
      this.loading = true
      let padding = this.padding,
          allowScrollZoom = this.allowScrollZoom,
          mapContainerId = this.mapContainerId,
          component = this

      map = new mapboxgl.Map({
        container: mapContainerId,
        style: "mapbox://styles/lsc-odga/cl8sv2oq8003i15p90nywwsu6?optimize=true",
        interactive: true,
        preserveDrawingBuffer: true
      })

      map.fitBounds( component.bbox, { padding: padding, duration:0 })

      if (allowScrollZoom === false) {
        map.scrollZoom.disable()
      }

      // ZOOM CONTROLS
      map.addControl(new mapboxgl.FullscreenControl({container: document.querySelector('#' + mapContainerId)}), 'top-left')
      const nav = new mapboxgl.NavigationControl({
        visualizePitch: true
      })
      map.addControl(nav, 'top-left')

      // LAYERS
      map.on('load', function () {
        component.initLayers()
        component.initGetHoverInfo()

        map.on('click', '119th_congress-fill', (e) => {
          let features = map.queryRenderedFeatures(e.point, { layers: ['119th_congress-fill'] })
          let cd = features[0].properties["geoid"]
          let urlSlug = process.env.NODE_ENV === "development" ? 'http://localhost:8080/congressional-district-profiles' : 'https://www.lsc.gov/node/4035'
          window.open(`${urlSlug}?embed=true&state=${cd.substring(0,2)}&district=${cd}`, "_parent")
        })

        setTimeout(() => { 
          component.loading = false 
          component.loaded = true
        }, 100)
      })
    },

    async initLayers () {
      this.layers.forEach(layer => {
        if (layer.loadOnStart === true) {
          map.addSource(`${layer.layerDefs['source']}`, {
            type: 'vector',
            url: `mapbox://${layer.tilesetId}`
          })
          map.addLayer(layer.layerDefs)
        }
      })
      this.filterLayers()
    },

    fitBounds (bbox) {
      if (map) {
        map.fitBounds( bbox, { padding: this.padding, duration:0 })
      }
    },

    filterLayers () {
      if (map) {
        try {
          map.setFilter('119th_congress-fill', this.filterStateField)
          
        } catch (err) {
          console.log(err)
        }
      }
    },

    styleSelectedCD () {
      if (map) {
        try {
          map.setPaintProperty('119th_congress-fill', 'fill-color', this.colorExp)
        } catch (err) {
          console.log(err)
        }
      }
    },

    initGetHoverInfo () {
      map.on('mousemove', (e) => {
        const canvas = map.getCanvas();

        const features = map.queryRenderedFeatures(e.point)
        
        let hoverTitle = null

        // Show office city or else county
        if (features.filter(x => x.layer.id === '119th_congress-fill').length > 0) {
          let geoid = features.filter(x => x.layer.id === '119th_congress-fill')[0].properties.geoid
          hoverTitle = `${this.getHoverStateName(geoid)} ${this.getDistrictLabel(geoid)}`
        }

        const node = document.querySelector('#map-tooltip')
        const nodeTitle = document.querySelector('#map-tooltip-title')
        const nodeSubtitle = document.querySelector('#map-tooltip-subtitle')

        if (hoverTitle !== null) {
          canvas.style.cursor = 'pointer'
          nodeTitle.textContent = hoverTitle
          nodeSubtitle.textContent = 'Click to view the snapshot'
          node.style.left = `${e.point.x + 10}px`
          node.style.top = `${e.point.y + 10}px`
          node.style.display = 'block'
        } else {
          canvas.style.cursor = ''
          node.style.display = 'none'
          nodeTitle.textContent = ''
        }
      })

      // Clear the popup when leaving the map area
      document.getElementById('map-container').addEventListener('mouseleave', () => {
        document.getElementById('map-tooltip').style.display = 'none'
      })
    },

    getDistrictLabel (districtGeoid) {
      let cd = districtGeoid.substring(2, 4)
      let cdNum = Number(cd)

      if (cd === '98') { return 'non-voting district' }
      else if (cd === '00') { return 'at-large district' }
      else { return `${cdNum}${this.nth(cdNum)} district` }
    },

    getHoverStateName (districtGeoid) { 
      return states.filter(x => x.fips === districtGeoid.substring(0, 2))[0].state
    },

    nth(n){
      return["st","nd","rd"][((n+90)%100-10)%10-1]||"th"
    },
  },

  watch: {
    newBBox(to) {
      this.fitBounds(to)
    },
    selectedState() {
      this.newBBox = bboxes.states[this.selectedState]
      this.filterLayers()
    },
    selectedDistrict() {
      this.styleSelectedCD()
    },
  }
}
</script>

<style lang="scss" scoped>
#profiles-landing-page-map {
  position: relative;
  width: 100%;
  max-width: 50rem;
  padding: 0 1.5rem;

  #map-container {
    border-radius: 0.625rem;
  }

  #map-tooltip {
    position: absolute;
    padding: 0.25rem 0.5rem;
    background-color: rgba(250, 250, 250, 0.8);
    border-radius: 0.5rem;
    pointer-events: none;

    #map-tooltip-subtitle {
      font-size: 0.9rem;
      color: #5a5a5a;
      font-style: italic;
    }
  }
}
</style>